import React, { useState, useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  ArrowLeft, 
  ArrowRight, 
  Loader2,
  Building2,
  Plus
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSharePointConfig } from "@/contexts/SharePointConfigContext";
import { apiService, Engagement, AuditClient } from "@/services/api-service";
import { TitleBar } from "@/components/TitleBar";

interface EngagementSelectionSearch {
  clientId?: string;
}

export default function EngagementSelectionPage() {
  const { user, isAuthenticated } = useAuth();
  const { loadConfig } = useSharePointConfig();
  const navigate = useNavigate();
  const search = useSearch({ from: "/engagements" }) as EngagementSelectionSearch;
  
  const [clients, setClients] = useState<Array<{
    client: AuditClient;
    engagements: Engagement[];
  }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(search.clientId || null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: "/login" });
      return;
    }

    loadEngagements();
  }, [isAuthenticated, navigate]);

  const loadEngagements = async () => {
    try {
      setIsLoading(true);
      setError("");
      
      // Get all engagements for the user
      const engagements = await apiService.getUserEngagements();
      
      // Group engagements by client
      const clientMap = new Map<string, { client: AuditClient; engagements: Engagement[] }>();
      
      // Get unique client IDs
      const clientIds = [...new Set(engagements.map(e => e.audit_client_id))];
      
      // Fetch client details for each client
      const allClients = await apiService.getAllClients();
      
      for (const clientId of clientIds) {
        const client = allClients.find(c => c.id === clientId);
        if (client) {
          const clientEngagements = engagements.filter(e => e.audit_client_id === clientId);
          clientMap.set(clientId, {
            client,
            engagements: clientEngagements,
          });
        }
      }
      
      setClients(Array.from(clientMap.values()));
      
      // If a specific client is selected, ensure it's in the list
      if (search.clientId && !clientMap.has(search.clientId)) {
        const client = allClients.find(c => c.id === search.clientId);
        if (client) {
          setClients([{ client, engagements: [] }]);
        }
      }
    } catch (err: any) {
      console.error("Error loading engagements:", err);
      setError(err.message || "Failed to load engagements");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEngagementSelect = async (engagement: Engagement, client: AuditClient) => {
    try {
      // Load SharePoint config for this engagement
      await loadConfig(
        engagement.id,
        client.client_name,
        engagement.engagement_name || `Engagement ${engagement.id.substring(0, 8)}`
      );
      
      // Navigate to engagement detail page
      navigate({ to: "/engagement/$engagementId", params: { engagementId: engagement.id } });
    } catch (err: any) {
      console.error("Error loading engagement config:", err);
      setError(err.message || "Failed to load engagement configuration");
    }
  };

  // All users can now create engagements - no role check needed

  const handleCreateEngagement = (clientId: string) => {
    navigate({ to: "/", search: { createEngagement: true, clientId } });
  };

  if (isLoading) {
    return (
      <>
        <TitleBar />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4 pt-10">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading engagements...</p>
          </div>
        </div>
      </>
    );
  }

  // Filter to show only selected client if one is selected
  const displayClients = selectedClientId
    ? clients.filter(c => c.client.id === selectedClientId)
    : clients;

  return (
    <>
      <TitleBar />
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4 pt-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate({ to: "/clients" })}
                className="text-white/60 hover:text-white mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Clients
              </Button>
              <h1 className="text-3xl font-bold text-white mb-2">Select Engagement</h1>
              <p className="text-muted-foreground">
                Choose an engagement to continue
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-md">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {displayClients.length === 0 ? (
            <Card className="bg-black/95 border-white/10">
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-white/40" />
                <p className="text-muted-foreground">No engagements found.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {displayClients.map(({ client, engagements }) => (
                <div key={client.id} className="space-y-4">
                  {/* Client Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-6 w-6 text-blue-500" />
                      <div>
                        <h2 className="text-xl font-semibold text-white">{client.client_name}</h2>
                        <Badge 
                          variant={client.status === 'Active' ? 'default' : 'secondary'}
                          className="mt-1"
                        >
                          {client.status}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleCreateEngagement(client.id)}
                      size="sm"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Engagement
                    </Button>
                  </div>

                  {/* Engagements Grid */}
                  {engagements.length === 0 ? (
                    <Card className="bg-black/95 border-white/10">
                      <CardContent className="p-6 text-center">
                        <p className="text-muted-foreground">No engagements available for this client.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {engagements.map((engagement) => (
                        <Card
                          key={engagement.id}
                          className="bg-black/95 border-white/10 hover:border-blue-500/50 transition-colors cursor-pointer"
                          onClick={() => handleEngagementSelect(engagement, client)}
                        >
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <FileText className="h-6 w-6 text-blue-500" />
                              <ArrowRight className="h-5 w-5 text-white/40" />
                            </div>
                            <CardTitle className="text-white mt-4">
                              {engagement.engagement_name || `Engagement ${engagement.id.substring(0, 8)}`}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-center justify-between">
                              <Badge 
                                variant={engagement.status === 'Active' ? 'default' : 'secondary'}
                              >
                                {engagement.status}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

